import {
  MIDPOINT_MEDIA_CASE_STUDY_IDS,
  midpointMediaCaseStudyDefaults,
} from "../content/midpointMediaCaseStudies.js";
import {
  MIDPOINT_MEDIA_MARKETING_HOME_LAYOUT,
  MIDPOINT_MEDIA_SLUG,
} from "../content/midpointMediaSiteContent.js";

/** Default GA4 property for Midpoint Media public Client View. */
export const MIDPOINT_MEDIA_GA4_MEASUREMENT_ID = "G-NXYGQXSFCL";
export const MIDPOINT_MEDIA_GA4_ENV_KEY = "REACT_APP_MIDPOINT_MEDIA_GA4_ID";

function getHomePage(manifest) {
  return (manifest?.pages || []).find((page) => page.template === "home") || null;
}

export function isMidpointMediaExperienceManifest(manifest, slug = null) {
  if (slug === MIDPOINT_MEDIA_SLUG || manifest?.slug === MIDPOINT_MEDIA_SLUG) {
    return true;
  }
  const homePage = getHomePage(manifest);
  return homePage?.content?.homeLayout === MIDPOINT_MEDIA_MARKETING_HOME_LAYOUT;
}

function buildCaseStudyPage(id) {
  const study = midpointMediaCaseStudyDefaults[id];
  const title = study?.title ? `${study.title.split(":")[0].trim()} Case Study` : "Case Study";
  return {
    route: id === "golf-homes" ? "/golf-homes" : `/case-studies/${id}`,
    template: "content",
    title,
    seo: {
      title: `${title} | Midpoint Media`,
      description: study?.subtitle || "",
    },
    content: {
      pageLayout: "midpoint-media",
      section: id === "golf-homes" ? "golf-homes" : "case-study",
      caseStudyId: id,
    },
  };
}

/** Direct-access careers application (not linked from primary nav). */
export const MIDPOINT_MEDIA_VENEZUELA_CAREERS_PAGE = {
  route: "/careers/venezuela",
  template: "content",
  title: "Careers in Venezuela",
  seo: {
    title: "Careers in Venezuela | Midpoint Media",
    description:
      "Remote roles in Venezuela: customer service, IT professionals, software engineers, and data analysts.",
  },
  content: {
    pageLayout: "midpoint-media",
    section: "venezuela-careers",
  },
};

/**
 * Ensure Midpoint Media case-study routes exist in the runtime manifest.
 * Migrations seed these pages, but client enrichment keeps showcase links working
 * if a local/dev DB is missing a newer case study page.
 */
export function enrichMidpointMediaManifest(manifest, slug = null) {
  if (!manifest || !isMidpointMediaExperienceManifest(manifest, slug)) {
    return manifest;
  }

  const byRoute = new Map((manifest.pages || []).map((page) => [page.route, page]));
  for (const id of MIDPOINT_MEDIA_CASE_STUDY_IDS) {
    const page = buildCaseStudyPage(id);
    const existing = byRoute.get(page.route);
    if (!existing) {
      byRoute.set(page.route, page);
      continue;
    }
    byRoute.set(page.route, {
      ...existing,
      ...page,
      seo: { ...existing.seo, ...page.seo },
      content: { ...existing.content, ...page.content },
    });
  }

  const careers = MIDPOINT_MEDIA_VENEZUELA_CAREERS_PAGE;
  const existingCareers = byRoute.get(careers.route);
  byRoute.set(
    careers.route,
    existingCareers
      ? {
          ...existingCareers,
          ...careers,
          seo: { ...existingCareers.seo, ...careers.seo },
          content: { ...existingCareers.content, ...careers.content },
        }
      : careers
  );

  const existingCrm = manifest.crm || {};
  const existingRules = Array.isArray(existingCrm.autoOpportunityRules)
    ? [...existingCrm.autoOpportunityRules]
    : [];
  const careersRule = {
    leadType: "job_application",
    createOpportunity: true,
    opportunityType: "Venezuela Careers Application",
    priority: "Normal",
  };
  const careersRuleIdx = existingRules.findIndex(
    (rule) => rule?.leadType === careersRule.leadType
  );
  if (careersRuleIdx >= 0) {
    existingRules[careersRuleIdx] = {
      ...existingRules[careersRuleIdx],
      ...careersRule,
    };
  } else {
    existingRules.push(careersRule);
  }

  return {
    ...manifest,
    pages: [...byRoute.values()],
    crm: {
      ...existingCrm,
      leadCapture: {
        enabled: true,
        defaultSource: "Midpoint Media",
        defaultStatus: "New",
        notifyEmails: [],
        ...(existingCrm.leadCapture || {}),
      },
      autoOpportunityRules: existingRules,
    },
    analytics: {
      ...(manifest.analytics || {}),
      enabled: manifest.analytics?.enabled !== false,
      clientViewCode: manifest.analytics?.clientViewCode || MIDPOINT_MEDIA_SLUG,
      googleAnalytics: {
        ...(manifest.analytics?.googleAnalytics || {}),
        enabled: true,
        measurementId:
          (typeof manifest.analytics?.googleAnalytics?.measurementId === "string" &&
            manifest.analytics.googleAnalytics.measurementId.trim()) ||
          MIDPOINT_MEDIA_GA4_MEASUREMENT_ID,
        measurementIdEnvKey:
          manifest.analytics?.googleAnalytics?.measurementIdEnvKey ||
          MIDPOINT_MEDIA_GA4_ENV_KEY,
        dataSource: {
          enabled: manifest.analytics?.googleAnalytics?.dataSource?.enabled !== false,
          propertyIdEnvKey:
            manifest.analytics?.googleAnalytics?.dataSource?.propertyIdEnvKey ||
            "GA4_MIDPOINT_MEDIA_PROPERTY_ID",
          pollIntervalMinutes:
            manifest.analytics?.googleAnalytics?.dataSource?.pollIntervalMinutes || 60,
          syncLookbackDays:
            manifest.analytics?.googleAnalytics?.dataSource?.syncLookbackDays || 7,
          ...(manifest.analytics?.googleAnalytics?.dataSource || {}),
        },
      },
      tracking: {
        pageViews: true,
        ctaClicks: true,
        contactFormSubmissions: true,
        sourceAttribution: true,
        ...(manifest.analytics?.tracking || {}),
      },
    },
  };
}
